import React, { useRef, useEffect, useState } from 'react';
import { Shape } from '../../types/Shape';
import ResizeHandles from './ResizeHandles';

interface HTMLShapeBaseProps {
  shape: Shape;
  isSelected: boolean;
  showResizeHandles?: boolean;
  stagePos: { x: number; y: number };
  stageScale: number;
  isDrawing?: boolean; // 다른 객체를 그리는 중인지
  currentTool?: string; // 현재 선택된 도구
  isAnnotationMode?: boolean; // annotation 모드 활성화 여부 (드래그 비활성화)
  onDragPrepare?: (shapeId: string) => void; // iframe 비활성화용 (히스토리 저장 없음)
  onDragStart?: (shapeId: string) => void; // 실제 드래그 시작 시 호출 (히스토리 저장)
  onDragMove?: (shapeId: string, newX: number, newY: number) => void;
  onDragEnd?: (shapeId: string) => void;
  onResize?: (shapeId: string, newWidth: number, newHeight: number, newX?: number, newY?: number) => void;
  onResizeStart?: (shapeId: string) => void;
  onResizeEnd?: () => void;
  onClick?: (shapeId: string, e: React.MouseEvent) => void;
  onDoubleClick?: (shapeId: string, e: React.MouseEvent) => void;
  onContextMenu?: (shapeId: string, e: React.MouseEvent) => void;
  onMouseEnter?: (shapeId: string) => void;
  onMouseLeave?: () => void;
  onStageClick?: (canvasX: number, canvasY: number, e: React.MouseEvent) => void;
  children: React.ReactNode;
}

const HTMLShapeBase: React.FC<HTMLShapeBaseProps> = ({
  shape,
  isSelected,
  showResizeHandles = false,
  stagePos,
  stageScale,
  isDrawing = false,
  currentTool = 'select',
  isAnnotationMode = false,
  onDragPrepare,
  onDragStart,
  onDragMove,
  onDragEnd,
  onResize,
  onResizeStart,
  onResizeEnd,
  onClick,
  onDoubleClick,
  onContextMenu,
  onMouseEnter,
  onMouseLeave,
  onStageClick,
  children,
}) => {
  const shapeRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [shapeStart, setShapeStart] = useState({ x: 0, y: 0 });
  const [mouseDownPos, setMouseDownPos] = useState<{ x: number; y: number } | null>(null);
  const dragThreshold = 5; // 5px 이상 움직여야 드래그 시작

  // 드래그 시작
  // 드래그 시작 공통 로직
  const startDrag = (clientX: number, clientY: number) => {
    // 마우스 다운 위치 저장 (드래그 vs 클릭 구분용)
    setMouseDownPos({ x: clientX, y: clientY });
    setDragStart({ x: clientX, y: clientY });
    setShapeStart({ x: shape.x, y: shape.y });

    // 드래그 준비 상태 알림 (iframe 비활성화를 위해 즉시 호출, 히스토리 저장 없음)
    if (onDragPrepare) {
      onDragPrepare(shape.id);
    }
  };

  // 스크롤 가능한 요소인지 확인하는 헬퍼 함수
  const isScrollable = (element: HTMLElement): boolean => {
    const style = window.getComputedStyle(element);
    const overflowY = style.overflowY;
    const overflowX = style.overflowX;

    // 세로 스크롤 가능 여부
    const isScrollableY = (overflowY === 'auto' || overflowY === 'scroll') &&
      element.scrollHeight > element.clientHeight;

    // 가로 스크롤 가능 여부
    const isScrollableX = (overflowX === 'auto' || overflowX === 'scroll') &&
      element.scrollWidth > element.clientWidth;

    return isScrollableY || isScrollableX;
  };

  // 클릭 지점이 실제 텍스트 위인지 확인하는 헬퍼 함수
  const checkIfOverText = (x: number, y: number, target: HTMLElement): boolean => {
    // 1. 명확한 텍스트 입력 요소인 경우
    if (['INPUT', 'TEXTAREA'].includes(target.tagName) || target.isContentEditable) {
      return true;
    }

    // 2. target 요소가 직접 텍스트를 포함하는지 확인
    // (자식 요소가 아닌 직접적인 텍스트 노드)
    const hasDirectTextContent = Array.from(target.childNodes).some(
      child => child.nodeType === Node.TEXT_NODE && child.textContent?.trim()
    );

    if (hasDirectTextContent) {
      // target이 텍스트를 직접 포함하면, 클릭 위치가 텍스트 영역 내인지 확인
      const targetRect = target.getBoundingClientRect();
      if (x >= targetRect.left && x <= targetRect.right &&
          y >= targetRect.top && y <= targetRect.bottom) {
        return true;
      }
    }

    // 3. 부모 요소 중 텍스트 컨테이너가 있는지 확인 (span, p, div with text 등)
    let parent = target.parentElement;
    while (parent && shapeRef.current?.contains(parent)) {
      const style = window.getComputedStyle(parent);
      // user-select: text인 요소 내부인지 확인
      if (style.userSelect === 'text') {
        const parentRect = parent.getBoundingClientRect();
        // 클릭 위치가 텍스트 컨테이너의 실제 콘텐츠 영역인지 확인
        if (x >= parentRect.left && x <= parentRect.right &&
            y >= parentRect.top && y <= parentRect.bottom) {
          // 추가 확인: 실제로 텍스트 콘텐츠가 있는지
          if (parent.textContent?.trim()) {
            return true;
          }
        }
      }
      parent = parent.parentElement;
    }

    return false;
  };

  // 휠 이벤트 핸들러: 스크롤 가능한 요소 내부에서는 캔버스 줌 전파 방지
  const handleWheel = (e: React.WheelEvent) => {
    let target = e.target as HTMLElement;

    // 타겟부터 shapeRef까지 올라가면서 확인
    while (target && target !== shapeRef.current && shapeRef.current?.contains(target)) {
      if (isScrollable(target)) {
        e.stopPropagation();
        return;
      }
      target = target.parentElement as HTMLElement;
    }
  };

  // 드래그 시작
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return; // 좌클릭만

    // Annotation 모드에서는 드래그 비활성화
    if (isAnnotationMode) {
      e.stopPropagation();
      return;
    }

    const target = e.target as HTMLElement;

    // 1. 링크 클릭 시 드래그 방지
    if (target.tagName === 'A' || target.closest('a')) {
      e.stopPropagation();
      return;
    }

    // 2. 스크롤 가능한 영역(내부 컨텐츠) 클릭 시 드래그 방지
    // (PDF 사이드바, 긴 텍스트 등) - 클릭 이벤트 등은 허용하되 드래그만 막음
    let current = target;
    while (current && current !== shapeRef.current && shapeRef.current?.contains(current)) {
      if (isScrollable(current)) {
        e.stopPropagation();
        return;
      }
      current = current.parentElement as HTMLElement;
    }

    // 화살표 도구일 때는 Stage 클릭 이벤트로 전파 (시작점 스냅을 위해)
    if (currentTool === 'arrow' && onStageClick && shapeRef.current) {
      // shapeRef의 실제 화면 위치를 가져옴
      const shapeRect = shapeRef.current.getBoundingClientRect();

      // shape 컨테이너 내부에서의 클릭 위치
      const shapeLocalX = e.clientX - shapeRect.left;
      const shapeLocalY = e.clientY - shapeRect.top;

      // shape 컨테이너 내부 좌표를 캔버스 좌표로 변환
      const canvasX = shape.x + (shapeLocalX / stageScale);
      const canvasY = shape.y + (shapeLocalY / stageScale);

      onStageClick(canvasX, canvasY, e);
      e.stopPropagation(); // 중복 처리 방지
      return;
    }

    // 3. 드래그 조건 확인
    if (currentTool === 'select' && shapeRef.current) {
      // 텍스트가 없는 경우 -> 전체 영역 드래그 허용
      const hasText = (shape.label && shape.label.trim().length > 0) || (shape.markdown && shape.markdown.trim().length > 0);

      if (!hasText) {
        // 텍스트가 없으면 어디를 클릭하든 드래그 가능
        startDrag(e.clientX, e.clientY);
        e.stopPropagation();
        return;
      }

      // 텍스트가 있는 경우: 실제 텍스트 위인지만 확인
      // (가장자리 제한 없이, 글씨 위면 선택, 빈 공간이면 드래그)
      const isOverText = checkIfOverText(e.clientX, e.clientY, target);

      if (!isOverText) {
        // 텍스트가 아닌 빈 공간 → 드래그 가능
        e.preventDefault();
        startDrag(e.clientX, e.clientY);
        e.stopPropagation();
        return;
      }
      // 텍스트 위 → 텍스트 선택 허용 (아래로 진행)
    }

    // 4. 텍스트 선택 가능한 영역 클릭 시 (가장자리가 아닌 내부라면) -> 텍스트 선택 허용
    const computedStyle = window.getComputedStyle(target);
    if (computedStyle.userSelect === 'text' || computedStyle.cursor === 'text') {
      e.stopPropagation();
      return;
    }

    // 5. 그 외
    e.stopPropagation();
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    // 화살표 도구일 때는 터치 처리하지 않음 (복잡성 방지)
    if (currentTool === 'arrow') return;

    // Annotation 모드에서는 드래그 비활성화
    if (isAnnotationMode) {
      e.stopPropagation();
      return;
    }

    if (e.touches.length === 1) {
      const touch = e.touches[0];
      const target = e.target as HTMLElement;

      // 1. 링크 터치 시 드래그 방지
      if (target.tagName === 'A' || target.closest('a')) {
        e.stopPropagation();
        return;
      }

      // 2. 스크롤 가능한 영역 터치 시 드래그 방지
      let current = target;
      while (current && current !== shapeRef.current && shapeRef.current?.contains(current)) {
        if (isScrollable(current)) {
          e.stopPropagation();
          return;
        }
        current = current.parentElement as HTMLElement;
      }

      // 3. 드래그 조건 확인
      if (currentTool === 'select' && shapeRef.current) {
        // 텍스트가 없는 경우 -> 전체 영역 드래그 허용
        const hasText = (shape.label && shape.label.trim().length > 0) || (shape.markdown && shape.markdown.trim().length > 0);

        if (!hasText) {
          startDrag(touch.clientX, touch.clientY);
          e.stopPropagation();
          return;
        }

        // 텍스트가 있는 경우: 실제 텍스트 위인지만 확인
        const isOverText = checkIfOverText(touch.clientX, touch.clientY, target);

        if (!isOverText) {
          // 텍스트가 아닌 빈 공간 → 드래그 가능
          e.preventDefault();
          startDrag(touch.clientX, touch.clientY);
          e.stopPropagation();
          return;
        }
        // 텍스트 위 → 텍스트 선택 허용 (아래로 진행)
      }

      // 4. 텍스트 선택 가능한 영역 터치 시 (내부) -> 드래그 방지
      const computedStyle = window.getComputedStyle(target);
      if (computedStyle.userSelect === 'text' || computedStyle.cursor === 'text') {
        e.stopPropagation();
        return;
      }

      // 5. 그 외 내부 (빈 공간 등)
      // 드래그 안 함
      e.stopPropagation();
    }
  };

  // 마우스 이동 감지 및 드래그
  // 마우스/터치 이동 감지 및 드래그
  useEffect(() => {
    if (!mouseDownPos) return;

    const handleMove = (clientX: number, clientY: number) => {
      // 드래그 시작 여부 판단
      if (!isDragging) {
        const dx = clientX - mouseDownPos.x;
        const dy = clientY - mouseDownPos.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance > dragThreshold) {
          setIsDragging(true);
          // 실제 드래그가 시작될 때 onDragStart 호출 (히스토리 저장)
          if (onDragStart) {
            onDragStart(shape.id);
          }
        }
      }

      if (isDragging) {
        const dx = (clientX - dragStart.x) / stageScale;
        const dy = (clientY - dragStart.y) / stageScale;

        const newX = shapeStart.x + dx;
        const newY = shapeStart.y + dy;

        if (onDragMove) {
          onDragMove(shape.id, newX, newY);
        }
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      handleMove(e.clientX, e.clientY);

      // 커서 동적 변경 (드래그 중이 아닐 때)
      if (!isDragging && shapeRef.current && currentTool === 'select' && !mouseDownPos) {
        const rect = shapeRef.current.getBoundingClientRect();

        // 마우스가 현재 shape 위에 있는지 확인
        if (
          e.clientX >= rect.left && e.clientX <= rect.right &&
          e.clientY >= rect.top && e.clientY <= rect.bottom
        ) {
          const hasText = (shape.label && shape.label.trim().length > 0) || (shape.markdown && shape.markdown.trim().length > 0);

          let isDraggable = false;

          if (!hasText) {
            // 텍스트가 없으면 전체 영역이 드래그 가능
            isDraggable = true;
          } else {
            // 텍스트가 있으면: 텍스트 위가 아니면 드래그 가능
            const target = e.target as HTMLElement;
            isDraggable = !checkIfOverText(e.clientX, e.clientY, target);
          }

          if (isDraggable) {
            shapeRef.current.dataset.borderHover = "true";
          } else {
            shapeRef.current.dataset.borderHover = "false";
          }
        } else {
          // shape 밖으로 나가면 해제
          if (shapeRef.current.dataset.borderHover === "true") {
            shapeRef.current.dataset.borderHover = "false";
          }
        }
      } else if (shapeRef.current && shapeRef.current.dataset.borderHover === "true") {
        // 드래그 중이거나 조건 불만족 시 해제
        shapeRef.current.dataset.borderHover = "false";
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.cancelable && isDragging) {
        e.preventDefault(); // 드래그 중 스크롤 방지
      }
      if (e.touches.length > 0) {
        const touch = e.touches[0];
        handleMove(touch.clientX, touch.clientY);
      }
    };

    const handleUp = () => {
      // 드래그가 시작되었거나 마우스 다운 상태였으면 드래그 종료 처리
      if ((isDragging || mouseDownPos) && onDragEnd) {
        onDragEnd(shape.id);
      }
      setIsDragging(false);
      setMouseDownPos(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleUp);
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleUp);
    };
  }, [mouseDownPos, isDragging, dragStart, shapeStart, stageScale, dragThreshold, shape.id, onDragPrepare, onDragStart, onDragMove, onDragEnd]);

  // 클릭 핸들러
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // 배경 클릭 핸들러로 전파 방지

    // 텍스트 선택 중이면 클릭 이벤트 무시 (도형 선택 토글 방지)
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) {
      return;
    }

    const target = e.target as HTMLElement;

    // 텍스트 위 클릭 시 도형 선택 방지
    if (currentTool === 'select' && shapeRef.current) {
      const hasText = (shape.label && shape.label.trim().length > 0) || (shape.markdown && shape.markdown.trim().length > 0);

      if (hasText) {
        // 텍스트 위인지만 확인 (가장자리 제한 없음)
        const isOverText = checkIfOverText(e.clientX, e.clientY, target);
        if (isOverText) {
          // 텍스트 위 클릭 → 도형 선택 안 함 (텍스트 선택 유지)
          return;
        }
        // 빈 공간 클릭 → 도형 선택 허용 (아래로 진행)
      }
    }

    if (onClick) {
      onClick(shape.id, e);
    }
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDoubleClick) {
      onDoubleClick(shape.id, e);
    }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onContextMenu) {
      onContextMenu(shape.id, e);
    }
  };

  // 계산된 위치와 크기
  const screenX = shape.x * stageScale + stagePos.x;
  const screenY = shape.y * stageScale + stagePos.y;
  const screenWidth = (shape.width || 100) * stageScale;
  const screenHeight = (shape.height || (shape.radius ? shape.radius * 2 : 100)) * stageScale;

  // 커서 스타일 결정
  const getCursor = () => {
    if (isDragging) return 'grabbing';
    if (currentTool === 'select') return 'grab';
    if (currentTool === 'arrow') return 'crosshair';
    return 'default';
  };

  return (
    <div
      ref={shapeRef}
      style={{
        position: 'absolute',
        left: screenX,
        top: screenY,
        width: screenWidth,
        height: screenHeight,
        zIndex: isAnnotationMode ? 99998 : (shape.order ?? 1) * 2,
        cursor: getCursor(),
        userSelect: 'none',
        pointerEvents: isDrawing ? 'none' : 'auto', // 그리기 중에는 마우스 이벤트 무시
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onClick={handleClick}
      onWheel={handleWheel}
      onDoubleClick={handleDoubleClick}
      onContextMenu={handleContextMenu}
      onMouseEnter={() => onMouseEnter && onMouseEnter(shape.id)}
      onMouseLeave={() => onMouseLeave && onMouseLeave()}
    >
      {children}

      {/* 선택 표시 */}
      {isSelected && (
        <div
          style={{
            position: 'absolute',
            top: -2,
            left: -2,
            right: -2,
            bottom: -2,
            border: '2px solid #3B82F6',
            pointerEvents: 'none',
            borderRadius: shape.type === 'circle' ? '50%' : '4px',
          }}
        />
      )}

      {/* 리사이즈 핸들 */}
      {showResizeHandles && onResize && onResizeEnd && (
        <ResizeHandles
          shape={shape}
          stageScale={stageScale}
          onResize={onResize}
          onResizeStart={onResizeStart}
          onResizeEnd={onResizeEnd}
        />
      )}
    </div>
  );
};

export default HTMLShapeBase;

